import React, { useEffect, useRef, useState } from 'react';
import { Building2, Save, Upload, X } from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { btnPrimary, errorCls, inputCls, labelCls } from '../../components/ui';
import { useAppPermisos } from '../../hooks/useAppPermisos';

interface ConfigEmpresa {
  nombre_emisor: string;
  nombre_comercial: string;
  nombre_planta: string;
  logo_url: string;
  telefono: string;
  correo_respuesta: string;
  contacto: string;
  codigo_exportador_default: string;
  ggn_global_gap_default: string;
}

const EMPTY_CONFIG: ConfigEmpresa = {
  nombre_emisor: '',
  nombre_comercial: '',
  nombre_planta: '',
  logo_url: '',
  telefono: '',
  correo_respuesta: '',
  contacto: '',
  codigo_exportador_default: '',
  ggn_global_gap_default: '',
};

export function ConfiguracionGeneral() {
  const empresaId = useEmpresaId();
  const { perm_administrar, loading: permisosLoading } = useAppPermisos();
  const fileRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<ConfigEmpresa>(EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError('');

      const primary = await supabase
        .from('fe_config_empresa')
        .select(
          'nombre_emisor, nombre_comercial, nombre_planta, logo_url, telefono, correo_respuesta, contacto, codigo_exportador_default, ggn_global_gap_default'
        )
        .eq('empresa_id', empresaId)
        .maybeSingle();

      let data: Record<string, unknown> | null = null;
      if (
        primary.error &&
        /codigo_exportador_default|ggn_global_gap_default/i.test(primary.error.message || '')
      ) {
        const fallback = await supabase
          .from('fe_config_empresa')
          .select(
            'nombre_emisor, nombre_comercial, nombre_planta, logo_url, telefono, correo_respuesta, contacto'
          )
          .eq('empresa_id', empresaId)
          .maybeSingle();
        data = (fallback.data as Record<string, unknown>) || null;
      } else {
        data = (primary.data as Record<string, unknown>) || null;
      }

      if (!active) return;
      if (data) {
        setForm({
          nombre_emisor: String(data.nombre_emisor || ''),
          nombre_comercial: String(data.nombre_comercial || ''),
          nombre_planta: String(data.nombre_planta || ''),
          logo_url: String(data.logo_url || ''),
          telefono: String(data.telefono || ''),
          correo_respuesta: String(data.correo_respuesta || ''),
          contacto: String(data.contacto || ''),
          codigo_exportador_default: String(data.codigo_exportador_default || ''),
          ggn_global_gap_default: String(data.ggn_global_gap_default || ''),
        });
      } else {
        setForm(EMPTY_CONFIG);
      }
      setLoading(false);
    }

    load();
    return () => {
      active = false;
    };
  }, [empresaId]);

  function setField<K extends keyof ConfigEmpresa>(key: K, value: ConfigEmpresa[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setOk(false);

    const payload = { empresa_id: empresaId, ...form };
    let save = await supabase.from('fe_config_empresa').upsert(payload, { onConflict: 'empresa_id' });
    let upsertError = save.error;

    if (
      upsertError &&
      /codigo_exportador_default|ggn_global_gap_default/i.test(upsertError.message || '')
    ) {
      const fallbackPayload = {
        empresa_id: empresaId,
        nombre_emisor: form.nombre_emisor,
        nombre_comercial: form.nombre_comercial,
        nombre_planta: form.nombre_planta,
        logo_url: form.logo_url,
        telefono: form.telefono,
        correo_respuesta: form.correo_respuesta,
        contacto: form.contacto,
      };
      save = await supabase
        .from('fe_config_empresa')
        .upsert(fallbackPayload, { onConflict: 'empresa_id' });
      upsertError = save.error;
    }

    setSaving(false);
    if (upsertError) {
      setError(upsertError.message);
      return;
    }

    setOk(true);
    window.dispatchEvent(new CustomEvent('empresa-config-updated'));
    setTimeout(() => setOk(false), 3000);
  }

  async function handleLogoUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('El archivo debe ser una imagen.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('El logo no debe superar 2 MB.');
      return;
    }

    setUploading(true);
    setError('');

    const extension = file.name.split('.').pop() || 'png';
    const path = `empresa_${empresaId}/logo.${extension}`;

    const upload = await supabase.storage
      .from('logos')
      .upload(path, file, { upsert: true, contentType: file.type });

    if (upload.error) {
      setUploading(false);
      setError(`Error al subir imagen: ${upload.error.message}`);
      return;
    }

    const { data } = supabase.storage.from('logos').getPublicUrl(path);
    setField('logo_url', `${data.publicUrl}?t=${Date.now()}`);

    if (fileRef.current) fileRef.current.value = '';
    setUploading(false);
  }

  const renderInput = (key: keyof ConfigEmpresa, label: string, placeholder = '') => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        className={inputCls}
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => setField(key, e.target.value)}
      />
    </div>
  );

  if (loading || permisosLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm" style={{ color: 'var(--ink-muted)' }}>
        Cargando configuracion...
      </div>
    );
  }

  if (!perm_administrar) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="text-lg font-semibold" style={{ color: 'var(--ink)' }}>Configuración General</h1>
        <p className="mt-1 text-xs" style={{ color: 'var(--ink-faint)' }}>Esta pantalla requiere permiso de administración del módulo.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-6 flex items-center gap-3">
        <Building2 size={22} style={{ color: 'var(--emp-accent)' }} />
        <h1 className="text-lg font-bold" style={{ color: 'var(--ink)' }}>
          Configuracion General
        </h1>
      </div>

      <form onSubmit={handleSave} className="flex flex-col gap-4">
        {renderInput('nombre_emisor', 'Razon social / emisor', 'Ej: Agropecuaria Vasquez y Zuniga, S. A.')}
        {renderInput('nombre_comercial', 'Nombre comercial', 'Ej: Thialez')}
        {renderInput('telefono', 'Telefono', 'Ej: 64095613')}
        {renderInput('correo_respuesta', 'Correo electronico', 'Ej: info@tialez.com')}
        {renderInput('contacto', 'Persona de contacto', 'Ej: Jesse Perez')}
        {renderInput(
          'codigo_exportador_default',
          'Codigo exportador por defecto',
          'Ej: 4128 o EXP-002'
        )}
        {renderInput(
          'ggn_global_gap_default',
          'GGN GlobalG.A.P. por defecto',
          'Ej: 4052852198479'
        )}

        <div>
          <label className={labelCls}>Logo de la empresa</label>
          <div className="mt-1 flex items-center gap-3">
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                flexShrink: 0,
                border: '2px dashed var(--line)',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--surface-raised)',
              }}
            >
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt="Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <Building2 size={24} style={{ color: 'var(--ink-faint)' }} />
              )}
            </div>

            <div className="flex flex-1 flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-2 rounded px-3 py-2 text-sm transition-colors"
                style={{
                  border: '1px solid var(--line)',
                  background: 'var(--surface-raised)',
                  color: 'var(--ink-muted)',
                }}
              >
                <Upload size={13} />
                {uploading ? 'Subiendo...' : 'Subir desde computadora'}
              </button>

              <input
                className={inputCls}
                value={form.logo_url}
                placeholder="https://..."
                onChange={(e) => setField('logo_url', e.target.value)}
              />
            </div>

            {form.logo_url && (
              <button
                type="button"
                title="Quitar logo"
                className="rounded p-1"
                style={{ color: 'var(--ink-faint)' }}
                onClick={() => setField('logo_url', '')}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <p className="mt-1 text-xs" style={{ color: 'var(--ink-faint)' }}>
            PNG o JPG cuadrado, maximo 2 MB. Se almacena en Supabase Storage (bucket logos).
          </p>
        </div>

        {error && <p className={errorCls}>{error}</p>}
        {ok && <p className="text-sm text-green-400">Configuracion guardada correctamente.</p>}

        <div className="flex justify-end pt-2">
          <button type="submit" disabled={saving || uploading} className={btnPrimary}>
            <Save size={14} /> {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}

